import React, { useState, useCallback, useMemo } from 'react';
import StripeCardForm from './StripeCardForm';
import { PaymentMethod } from '@/services/platformApi';
import { Separator } from '@vaiu/ui/separator';

interface PaymentMethodCardFormProps {
  amount: number;
  currency: string;
  onSubmit: (paymentMethod: PaymentMethod) => Promise<void>;
  onCancel?: () => void;
  isGuest?: boolean;
  disabled?: boolean;
  className?: string;
  showSeparator?: boolean;
  submitLabel?: string;
  loadingLabel?: string;
  skipPlatformSave?: boolean;
}

/**
 * PaymentMethodCardForm - Wrapper for StripeCardForm
 * Handles new card entry for payment
 */
const PaymentMethodCardFormComponent: React.FC<PaymentMethodCardFormProps> = ({
  amount,
  currency,
  onSubmit,
  onCancel,
  isGuest = false,
  disabled = false,
  className,
  showSeparator = true,
  submitLabel,
  loadingLabel,
  skipPlatformSave = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize handleSubmit to prevent Stripe CardElement re-renders
  const handleSubmit = useCallback(async (paymentMethod: PaymentMethod) => {
    if (disabled || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(paymentMethod);
    } finally {
      setIsSubmitting(false);
    }
  }, [disabled, isSubmitting, onSubmit]);

  return (
    <div className={className}>
      {showSeparator && (
        <div className="flex items-center gap-4 my-4">
          <Separator className="flex-1" />
          <span className="text-sm text-muted-foreground">OR</span>
          <Separator className="flex-1" />
        </div>
      )}
      
      <StripeCardForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        loading={isSubmitting}
        isGuest={isGuest}
        amount={amount}
        currency={currency}
        submitLabel={submitLabel}
        loadingLabel={loadingLabel}
        skipPlatformSave={skipPlatformSave}
      />
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const PaymentMethodCardForm = React.memo(PaymentMethodCardFormComponent);
